import { services } from '@cwos/api-client';
import { formatClock } from '@cwos/ui-shared';
import type { DeliveryPopulation, DeliveryScope, MissedVisit } from '@cwos/ui-admin';
import {
  ORGANIZATION,
  narrowedTo,
  populationPhrase,
  type FigurePhase,
  type Population,
} from './figureState.js';

/**
 * Derive "are today's services actually happening?" figures for DeliveryBand
 * from the real `/services/scheduled` (planned) and `/services/events`
 * (delivered) lists — the backend exposes no single today-summary endpoint, so
 * we count honestly from live rows here rather than fabricate a summary.
 * (undocumented / unmatched come from the reconciliation summary; see RightNow.)
 *
 * ---------------------------------------------------------------------------
 * THE TWO LISTS DO NOT COVER THE SAME PEOPLE, AND THIS USED TO ASSUME THEY DID.
 * ---------------------------------------------------------------------------
 * BOTH reads are scoped, and each is widened by a DIFFERENT key:
 *
 *   planned   `GET /services/scheduled` — organization-wide for a
 *             `schedule:manage` holder (the roster key, org_admin and
 *             supervisor), otherwise the caller's own worker row plus their
 *             direct reports.
 *   delivered `GET /services/events` — organization-wide for a
 *             `service:read_all` holder (org_admin alone), otherwise the same
 *             own-worker-plus-direct-reports frame.
 *
 * Both narrow in the SQL, so an out-of-scope row is MISSING from the response
 * rather than refused — nothing in the payload says it happened. Each response
 * therefore carries a `scoped` flag, and those two flags are what this function
 * takes: the frames are a fact about the ANSWER, and the only place they are
 * stated is the answer.
 *
 * WHAT THIS PARAGRAPH USED TO SAY, AND WHY IT MATTERED. It asserted that
 * `GET /services/scheduled` "is organization-wide, deliberately and
 * permanently", quoting `services/routes.ts`. The route no longer says that and
 * the grants no longer do it: the quoted migration is a WRITE migration that
 * settles who may BUILD the roster, and reading it as settling the READ left the
 * stock `worker` role enumerating every colleague's forward calendar. A comment
 * asserting a scope is exactly how the underlying defect survived review, so the
 * frames are now read from the response and never from prose.
 *
 * WHAT IS DIVIDED AND WHY IT IS DANGEROUS. One screen puts the two numbers side
 * by side and DIVIDES them. For a supervisor with four reports in a forty-visit
 * organization the band read "6 of 40", filled its meter to 15%, and showed "off
 * pace" from mid-morning on — three claims about the organization computed from
 * one population over another.
 *
 * `missed` was worse than misleading, because it is a SET DIFFERENCE: planned
 * rows minus the `scheduledServiceId`s present in the event list. A row the
 * DELIVERED read cannot see is indistinguishable from a visit nobody made, so it
 * comes out as a missed visit, with a clickable evidence link, in the band's
 * alarm channel. Those entries are not "computed over a wider population" — they
 * are false.
 *
 * The two narrowings break different things, and this is the distinction the
 * file turns on:
 *
 *   delivered narrow (`schedule:manage` without `service:read_all` — every
 *     shipped supervisor) breaks `missed` and `inProgress`: every visit a
 *     colleague outside the caseload delivered reads as a failure of care.
 *   planned narrow (`service:read_all` without `schedule:manage` — a
 *     tenant-custom compliance role) breaks the RATIO and the LABELS: every
 *     plan's event is still visible so no false missed visit appears, but
 *     `delivered` counts visits whose plans are not in `planned` at all. The
 *     band reads "10 of 2", the meter pins, the pace pill is permanently green,
 *     and a caseload's missed visits are headed as the organization's.
 *
 * THE PREDICATE IS "BOTH READS COVERED THE WHOLE ORGANIZATION", AND FRAME
 * EQUALITY IS NOT ENOUGH. Two narrowed reads do resolve through the same
 * `resolveWorkerScope` call on the same session user, so they cover the same
 * WORKERS — and that is still not sufficient, because the set difference does
 * not join on worker. It joins on `scheduledServiceId`, and the two lists are
 * narrowed on two different columns: `scheduled_service.worker_id` (who the
 * visit is PLANNED for) and `service_event.worker_id` (who DELIVERED it).
 * `createServiceEventSchema` takes those two ids independently, so a visit
 * planned for a worker inside the caseload and covered by one outside it has a
 * planned row in the narrowed list and an event that is not — and the
 * subtraction reports it as a missed visit that did not happen. That is the
 * exact false alarm this file exists to remove, reached by a different route.
 *
 * A narrowed PLANNED read against an organization-wide delivered one has the
 * opposite property: every plan's event is visible, so no false missed visit
 * appears, and what is wrong instead is the LABEL — "N missed visits" and
 * "{delivered} of {planned}" over a plan that is one caseload's, presented as
 * the organization's. Sound arithmetic under a false heading is not an answer
 * either.
 *
 * ANY OTHER FRAME, and the difference is `absent` — `figureState.ts`'s word for
 * "the response arrived and this figure was not in it … genuinely not
 * computable". It is deliberately not `refused` (nothing was withheld: both
 * reads succeeded completely for their own frame) and deliberately not a zero.
 * Those are three different statements and this file spends its length on the
 * difference between them. `comparable` is the one predicate, so the ratio, the
 * meter, the pace pill, `missed` and `inProgress` cannot come apart.
 *
 * WHY `planned` IS NOT NARROWED HERE INSTEAD. The client does not know the
 * caseload. Intersecting the planned rows with the worker ids seen in the event
 * list is unsound in the obvious direction: a worker who IS in the caseload but
 * has delivered nothing yet contributes no event row, so their genuinely
 * planned visits would vanish — the same class of error, reached by arithmetic
 * instead of by omission.
 */
export interface DeliveryToday {
  planned: number;
  delivered: number;
  /**
   * WHOSE plan `planned` counts, and whose deliveries `delivered` counts. Two
   * fields rather than one because the two reads are widened by two different
   * keys and a caller can hold one and not the other. `figureState.Population`
   * so a figure and its frame are spelled the same way here as on every tile.
   */
  plannedPopulation: Population;
  deliveredPopulation: Population;
  /**
   * Planned visits under way right now, as a FIGURE and not as a number.
   *
   * `{ kind: 'value' }` carries the population both figures share; `{ kind:
   * 'absent' }` is every other frame — not derivable, and therefore not a zero.
   * The type is why: there is no `value` to read on the absent variant, so a
   * caller cannot reach for one and find a zero waiting.
   */
  inProgress: FigurePhase;
  dayElapsedPct: number;
  /** The rows themselves. Empty whenever `missedCount` is not a value. */
  missed: MissedVisit[];
  /**
   * Whether `missed` is an answer, and over whom. `absent` means "not derivable
   * across these two frames", which an empty list cannot say on its own — and
   * reading that empty list as "no missed visits" is the reassurance this note
   * exists to prevent.
   */
  missedCount: FigurePhase;
  /**
   * Whether BOTH reads covered the whole organization — the only frame in which
   * `planned` and `delivered` count the same complete population, so the two may
   * be divided, metered and paced against each other, and the only frame in
   * which the set difference between them has no false positives. When false
   * the two are separate facts and nothing may be computed across them.
   */
  comparable: boolean;
  asOf: string;
}

/**
 * Which frame each of the two reads came back in.
 *
 * BOTH are required and neither is defaulted, for the reason `ListState` takes
 * `empty` and `failedMessage` as separate required props: the defect was an
 * omission, and a default would let the next caller reproduce it by not
 * thinking about it. `plannedCoversWholeOrganization` was the field that did not
 * exist, and its absence was read as "the planned list is organization-wide",
 * which is a permission decision made by a missing parameter.
 *
 * Each is the NEGATION of the `scoped` flag on the corresponding response
 * (`services.ScheduledRoster.scoped`, `services.ServiceEventPage.scoped`) — the
 * server's own statement of the frame, never a client-side re-derivation from a
 * permission list. A flattened key check reads a PROGRAM-scoped grant as
 * organization-wide, which is this repository's recurring defect class and was
 * how this screen decided the delivered frame until the server started saying.
 */
export interface DeliveryScopeInput {
  plannedCoversWholeOrganization: boolean;
  deliveredCoversWholeOrganization: boolean;
}

/**
 * The phrasing `worker-scope.ts` actually implements: the caller's own worker
 * row, plus workers whose `supervisor_id` points at them. `SupervisionCenter`
 * and `RightNow` say the same thing, and it is one string so they cannot drift.
 */
export const OWN_CASELOAD = 'you and the people you supervise';

function frame(coversWholeOrganization: boolean): Population {
  return coversWholeOrganization ? ORGANIZATION : narrowedTo(OWN_CASELOAD);
}

/** Exported only so `localDayWindow` below can be proved equivalent to it. */
export function sameLocalDay(iso: string, ref: Date): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate()
  );
}

/**
 * The SAME interval `sameLocalDay` tests, expressed as two instants the server
 * can be given.
 *
 * It lives next to `sameLocalDay` and is derived from the same `ref` because the
 * two must not drift: the server-side window on `GET /services/scheduled` and
 * `GET /services/events` is only a transfer reduction — rather than a change to
 * what Right Now shows — for exactly as long as it selects the rows this
 * predicate would have kept. `deliveryWindowIsSameLocalDay` in
 * `delivery.test.ts` is the assertion, and it fails if either definition moves.
 *
 * `[00:00:00.000, 23:59:59.999]` in the BROWSER's zone, closed at both ends,
 * matching the closed comparison in `listScheduledServices` /
 * `listServiceEvents`. Local, not the organization's zone, because
 * `sameLocalDay` reads `getFullYear` / `getMonth` / `getDate` — a worker's own
 * calendar day is what this band has always meant.
 */
export function localDayWindow(ref: Date): { from: string; to: string } {
  const from = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate(), 0, 0, 0, 0);
  const to = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate(), 23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * Still something to do, for the two operational figures (`missed`,
 * `inProgress`). A calendar-backed visit answers by its canonical bucket and
 * lifecycle (`isVisitClosed`): an imported visit's `status` stays 'scheduled'
 * when the EHR cancels or completes it. Only a native visit — no bucket — is
 * also closed by an event recorded against it.
 */
const SETTLE_LAG_MS = services.SETTLE_LAG_HOURS * 3_600_000;

/** Booked in, and charted by, an EHR: its outcome arrives on the EHR's schedule, not at its end time. */
function isEhrBooked(s: services.ScheduledService): boolean {
  return s.source === 'imported' || s.integrationId != null || s.deliveredBucket != null;
}

function stillOpen(s: services.ScheduledService, withEvent: ReadonlySet<string>): boolean {
  if (services.isVisitClosed(s)) return false;
  return !(s.deliveredBucket == null && withEvent.has(s.id));
}

export function deriveDeliveryToday(
  scheduled: services.ScheduledService[],
  events: services.ServiceEvent[],
  now: Date,
  scope: DeliveryScopeInput,
  /** `ServiceEventPage.calendarIntegrationIds` — the tenant's calendars for this window. */
  calendarIntegrationIds: readonly string[] = [],
): DeliveryToday {
  const plannedPopulation = frame(scope.plannedCoversWholeOrganization);
  // BOTH, and not either alone. See the header: a narrowed DELIVERED read makes
  // the difference produce missed visits that did not happen, and a narrowed
  // PLANNED read makes a sound difference carry a false heading.
  const comparable = scope.plannedCoversWholeOrganization && scope.deliveredCoversWholeOrganization;
  const shared = comparable ? ORGANIZATION : null;

  // SCHEDULED WORK, not every row (owner ruling 2026-10-07): a cancelled visit —
  // by Phibian or by the EHR — and one the EHR no longer lists are not planned.
  const todaysPlanned = scheduled.filter(
    (s) => sameLocalDay(s.startsAt, now) && services.isScheduledWork(s),
  );
  const todaysEvents = events.filter((e) => sameLocalDay(e.occurredAt, now));

  const scheduledIdsWithEvent = new Set(
    todaysEvents.map((e) => e.scheduledServiceId).filter((id): id is string => id !== null),
  );

  // THE CANONICAL DELIVERED (services/deliveredServices.ts): a calendar-backed
  // visit by its occurrence bucket — counted once, whatever its encounters —
  // and every other visit by its own delivered event. Counting encounters
  // counted a Ritten-cancelled visit's stray encounter and missed a delivered
  // occurrence with none.
  // From the SERVER (a tenant fact): a narrowed reader's roster, or a day with
  // no occurrence on it, would otherwise let a calendar-covered encounter count
  // on its own. The planned rows' own installations only cover an older server.
  const calendarIntegrations = new Set([
    ...calendarIntegrationIds,
    ...scheduled.flatMap((s) => (s.integrationId ? [s.integrationId] : [])),
  ]);
  const deliveredOccurrences = todaysPlanned.filter(
    (s) => s.deliveredBucket === 'delivered',
  ).length;
  const deliveredOwn = todaysEvents.filter((e) =>
    services.isOwnDelivery(e, calendarIntegrations),
  ).length;
  const delivered = deliveredOccurrences + deliveredOwn;
  // A calendar-backed organization's deliveries are read off the PLANNED roster,
  // so that is the frame they are counted in.
  // ponytail: a hybrid tenant (calendar AND native deliveries) under two
  // different frames keeps the events frame; split the figure if one appears.
  const deliveredFromRoster = calendarIntegrations.size > 0 && deliveredOwn === 0;

  const deliveredPopulation = deliveredFromRoster
    ? plannedPopulation
    : frame(scope.deliveredCoversWholeOrganization);

  // Both of the derivations below subtract the event list from the planned
  // list. That is an answer only when BOTH lists are complete; a row either one
  // could not see reads as a failure of care that nobody can check.
  const missed: MissedVisit[] = !shared
    ? []
    : todaysPlanned
        .filter((s) => {
          if (!stillOpen(s, scheduledIdsWithEvent)) return false;
          // CORE-AUD-221: an EHR-booked visit is charted in the EHR, hours to
          // days later. Until SETTLE_LAG_HOURS past its start it is "not
          // charted yet", never missed — the canonical `isDue` (scheduled-work.ts).
          if (isEhrBooked(s)) {
            return new Date(s.startsAt).getTime() < now.getTime() - SETTLE_LAG_MS;
          }
          const end = s.endsAt ? new Date(s.endsAt) : new Date(s.startsAt);
          return end.getTime() < now.getTime();
        })
        .map((s) => ({
          id: s.id,
          label: `Visit at ${formatClock(new Date(s.startsAt))}`,
          // The Monitor view's reconciliation queue is where a visit without
          // its record is actually worked. This used to say
          // /delivery?evidence=visit:<id> — no /delivery route exists and
          // nothing consumes ?evidence=, so the link landed on the coming-soon
          // panel (L107 found the same defect on two RightNow tiles). A real
          // destination beats a per-visit deep link nothing serves.
          href: '/dashboard?view=monitor',
        }));

  const inProgress: FigurePhase = !shared
    ? { kind: 'absent' }
    : {
        kind: 'value',
        population: shared,
        value: todaysPlanned.filter((s) => {
          if (!stillOpen(s, scheduledIdsWithEvent)) return false;
          const start = new Date(s.startsAt).getTime();
          const end = s.endsAt ? new Date(s.endsAt).getTime() : start;
          return start <= now.getTime() && now.getTime() <= end;
        }).length,
      };

  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayElapsedPct = ((now.getTime() - midnight) / 86_400_000) * 100;

  return {
    planned: todaysPlanned.length,
    delivered,
    plannedPopulation,
    deliveredPopulation,
    inProgress,
    dayElapsedPct,
    missed,
    missedCount: shared
      ? { kind: 'value', value: missed.length, population: shared }
      : { kind: 'absent' },
    comparable,
    asOf: formatClock(now),
  };
}

/**
 * The two frames above, said in the vocabulary `DeliveryBand` speaks.
 *
 * THE BAND ASKS A QUESTION THIS FILE ALREADY KNOWS THE ANSWER TO, and until now
 * the screen answered it with the weakest of the three available replies for
 * every reader who was not an org_admin. `DeliveryScope` has three states and
 * `RightNow` used two of them: `organization` when both reads were whole, and
 * `narrowed` — "these count different people, nothing may be divided" — for
 * everything else, with no planned frame attached. For the shipped supervisor,
 * whose two reads cover the SAME caseload, that is a sound ratio withheld and
 * replaced by a sentence describing a divergence that is not happening. This
 * function is the missing arm.
 *
 * THE `shared` ARM LICENSES THE RATIO AND NOTHING ELSE, AND THAT IS THE WHOLE
 * DIFFICULTY. `comparable` — "both reads covered the whole organization" — is a
 * STRICTER predicate than "both reads came back in the same frame", and it has
 * to stay stricter, because the two figures are combined in two different ways
 * and only one of them is a division:
 *
 *   THE RATIO joins nothing. It is `delivered / planned`, two cardinalities over
 *     one population, and identical framing is sufficient for it: if both reads
 *     cover the same caseload, "6 of 10 for your caseload" is true.
 *   `missed` AND `inProgress` JOIN, on `scheduledServiceId` — and the two lists
 *     are narrowed on two DIFFERENT columns, `scheduled_service.worker_id`
 *     versus `service_event.worker_id`. A visit planned for a worker inside the
 *     caseload and covered by a colleague outside it has a planned row in the
 *     narrowed roster and no event in the narrowed event list, so the set
 *     difference reports a missed visit, with an evidence link, that did not
 *     happen. Two identically-framed reads still produce that phantom. See the
 *     header of this file, which exists for it.
 *
 * So the frames decide the band's SCOPE and `comparable` decides its FIGURES,
 * and this function is deliberately not allowed to touch the second. It returns
 * a `DeliveryScope` and nothing else; `missedCount` and `inProgress` stay the
 * `FigurePhase`s `deriveDeliveryToday` computed under `comparable`, which is
 * why a `shared` reader gets the ratio and a dash where the missed count would
 * be. Deriving `missedKnown` from `scope.kind !== 'narrowed'` at the call site
 * would look like a tidy-up and would put the phantom straight back.
 *
 * `unknown` OMITS THE PLANNED FRAME RATHER THAN GUESSING ONE. `Population` has a
 * third member — the frame is not yet known — and `DeliveryPopulation` has no
 * spelling for it, by design: the band's `planned` field is optional precisely
 * so that "not stated" can be said, and its own comment records that an omitted
 * frame must never be read as the organization. Mapping `unknown` onto
 * `organization` to satisfy the type would be the original defect, re-entered
 * through a total function.
 */
function bandPopulation(population: Population): DeliveryPopulation | null {
  switch (population.kind) {
    case 'organization':
      return { kind: 'organization' };
    case 'narrowed':
      return { kind: 'narrowed', scope: population.who };
    case 'unknown':
      // Not stated. There is no third `DeliveryPopulation` and there must not
      // be a guess; the caller below omits the field.
      return null;
  }
}

/**
 * Which of the band's three states these two frames put it in.
 *
 * EQUIVALENCE IS COMPARED ON THE NOUN PHRASE, not on the `Population` objects,
 * because the phrase is what the band prints and what a reader is asked to
 * believe. `OWN_CASELOAD` is a single exported constant for exactly this
 * reason: both frames are built from it by `frame()` above, so two narrowed
 * reads by the same reader are the same string by construction rather than by
 * coincidence, and a future narrowing that says something different — a program
 * name, say — falls out of `shared` on its own instead of being quietly
 * declared equivalent.
 */
export function deliveryBandScope(planned: Population, delivered: Population): DeliveryScope {
  const plannedFrame = bandPopulation(planned);
  const deliveredFrame = bandPopulation(delivered);

  if (plannedFrame?.kind === 'organization' && deliveredFrame?.kind === 'organization') {
    return { kind: 'organization' };
  }

  if (
    plannedFrame?.kind === 'narrowed' &&
    deliveredFrame?.kind === 'narrowed' &&
    plannedFrame.scope === deliveredFrame.scope
  ) {
    return { kind: 'shared', scope: deliveredFrame.scope };
  }

  // Different populations, or a frame nobody has stated. `scope` is the
  // DELIVERED frame, which this variant has always carried; `planned` is
  // attached only when there is a frame to attach, so an unknown planned read
  // reaches the band as silence rather than as an organizational claim.
  return plannedFrame
    ? { kind: 'narrowed', scope: populationPhrase(delivered), planned: plannedFrame }
    : { kind: 'narrowed', scope: populationPhrase(delivered) };
}
